/* eslint-disable clubmed/no-fake-component */
import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { t } from 'Src/i18n';
import { ICONS } from 'Components/utils/icon/icons';
import { Badge } from 'Components/frameworks/badge/Badge';
import { Notification } from 'Components/frameworks/notification/Notification';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { transformMinutesInHours } from 'Components/widgets/transport-details/transportUtils';
import { getInitiallySelectedTransfer, getTransfersWithLabel } from './transferUtils';
import { TransferSelection } from './TransferSelection';
type TransfersSectionProps = {
  isBE?: boolean;
  availableTransfers: ReadonlyArray<UpsellTransfer>;
  selectedTransfers: ReadonlyArray<UpsellTransfer>;
  addTransfer: (arg0: UpsellTransfer) => Promise<unknown>;
  removeTransfer: (arg0: UpsellTransfer) => Promise<unknown>;
  setHasSelectedTransfer?: (arg0: boolean) => void;
  bookingId?: string;
};
export const TransfersSection = ({
  addTransfer,
  availableTransfers,
  bookingId,
  removeTransfer,
  selectedTransfers,
  isBE = false,
  setHasSelectedTransfer,
}: TransfersSectionProps) => {
  const addedTransfer = selectedTransfers?.[0];
  const initiallySelectedTransfer = getInitiallySelectedTransfer({
    isBE,
    addedTransfer,
    selectedTransfers,
    availableTransfers,
  });
  const [selectedTransfer, setSelectedTransfer] = React.useState<UpsellTransfer | null>(
    initiallySelectedTransfer,
  );
  const { productInfo: { durationInMinutes = '', vehicle, description } = {} } =
    selectedTransfer || {};
  const { label: vehicleLabel = '' } = vehicle || {};
  const shouldDisplayTransferInformationMessage = durationInMinutes || vehicleLabel;
  const durationInHours = transformMinutesInHours(durationInMinutes);
  const transferInformationMessageKey = vehicleLabel
    ? 'durationAndMeanOfTransportation'
    : 'duration';
  const validTransfers = getTransfersWithLabel(availableTransfers);
  useEffect(() => {
    if (setHasSelectedTransfer) {
      setHasSelectedTransfer(!!selectedTransfer);
    }
  }, [selectedTransfer]);

  if (isEmpty(availableTransfers) && !selectedTransfer) {
    return null;
  }

  return !isEmpty(validTransfers) ? (
    <section className="rounded-16 bg-lightSand p-20" data-testid="TransfersSection">
      <TransferSelection
        addedTransfer={addedTransfer}
        bookingId={bookingId}
        validTransfers={validTransfers}
        selectedTransfer={selectedTransfer}
        addTransfer={addTransfer}
        removeTransfer={removeTransfer}
        setSelectedTransfer={setSelectedTransfer}
      />
      {shouldDisplayTransferInformationMessage && (
        <div className="mt-20 flex gap-20">
          <Badge
            className="size-48 border-black bg-lightSand text-black"
            iconName={ICONS.CLOCK}
            iconSize="24px"
            variant="pill"
          />
          <div className="text-start text-16 leading-normal">
            <TemplateString
              template={t(`common.transport.transfer.${transferInformationMessageKey}`)}
              values={{
                duration: durationInHours,
                label: vehicleLabel,
              }}
              valuesClassName={{
                duration: 'font-bold',
                label: 'font-bold',
              }}
            />
            <p className="mt-8 text-14 text-mediumGrey">
              {`${t('common.transport.transfer.note').trim()} ${description}`}
            </p>
            <p className="mt-8 text-14 text-mediumGrey">
              {t('common.transport.transfer.waitingTime')}
            </p>
          </div>
        </div>
      )}
    </section>
  ) : (
    <Notification className="text-orange" title={t('booking.form.errors.transferUnavailable')} />
  );
};
